/**
 * IdentifiersSettingsTab - Settings > Identifiers (WP1-12).
 *
 * Tenant-configurable human-readable IDs for registrations (holdings) and
 * claims. Per resource: an on/off toggle, a live specimen ID, a reorderable
 * pattern of segments (prefix / year / code / number), and per-slot code grids
 * (category / product type / fixed) with live previews. Registration and claim
 * prefixes must be disjoint - overlaps are flagged inline before save,
 * mirroring the server rule.
 */
import { Fragment, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import { toast } from 'sonner'
import { ChevronLeft, ChevronRight, Loader2, Plus, X } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Switch } from '@/shared/components/ui/switch'
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { toastApiError } from '@/shared/lib/api-error'
import { cn } from '@/shared/lib/utils'
import {
  useIdentifierConfig,
  useUpdateIdentifierScheme,
  type IdentifierCodeInput,
  type IdentifierCodeSourceKind,
  type IdentifierConfig,
  type IdentifierPrefixSource,
  type IdentifierResourceType,
  type IdentifierScheme,
  type IdentifierSchemePayload,
  type IdentifierSegment,
  type IdentifierSlot,
  type IdentifierSourceOption,
  type IdentifierYearMode,
} from '@/admin/hooks/use-identifier-config'
import { VoucherIdentifiersSection } from '@/admin/pages/settings/voucher-identifiers-section'

// ── Draft model ──────────────────────────────────────────

const RESOURCES = ['holding', 'claim'] as const

const ALL_SEGMENTS: IdentifierSegment[] = ['prefix', 'year', 'code', 'seq']
const DEFAULT_ORDER: IdentifierSegment[] = ['prefix', 'year', 'seq']

interface SchemeDraft {
  is_enabled: boolean
  segment_order: IdentifierSegment[]
  prefix_source: IdentifierPrefixSource
  code_source: IdentifierPrefixSource | ''
  year_mode: IdentifierYearMode
  padding: number
  start_at: number | null
  /** `${source_kind}:${ref_key}` -> code (uppercase alnum, max 8), prefix slot. */
  codes: Record<string, string>
  /** Same shape for the second resolved slot ("code"). */
  codeCodes: Record<string, string>
}

type DraftMap = Record<IdentifierResourceType, SchemeDraft>

/** Live next_value previews, one map per slot. */
type NextValues = { prefix: Record<string, number>; code: Record<string, number> }

const codeKey = (kind: IdentifierCodeSourceKind, refKey: string) =>
  `${kind}:${refKey}`

const DEFAULT_CODE: Record<IdentifierResourceType, string> = {
  holding: 'REG',
  claim: 'CLM',
}

const DEFAULT_SOURCE: Record<IdentifierResourceType, IdentifierPrefixSource> = {
  holding: 'category',
  claim: 'static',
}

function draftFromScheme(
  resource: IdentifierResourceType,
  scheme: IdentifierScheme | null,
): SchemeDraft {
  const codes: Record<string, string> = {
    [codeKey('fallback', '')]: DEFAULT_CODE[resource],
    [codeKey('static', '')]: DEFAULT_CODE[resource],
  }
  const codeCodes: Record<string, string> = {}
  for (const c of scheme?.codes ?? []) {
    const target = c.slot === 'code' ? codeCodes : codes
    target[codeKey(c.source_kind, c.ref_key)] = c.code
  }
  const order = scheme?.segment_order?.length ? scheme.segment_order : DEFAULT_ORDER
  return {
    is_enabled: scheme?.is_enabled ?? false,
    segment_order: [...order],
    prefix_source: scheme?.prefix_source ?? DEFAULT_SOURCE[resource],
    code_source: scheme?.code_source ?? '',
    year_mode: scheme?.year_mode ?? 'yyyy',
    padding: scheme?.padding ?? 8,
    start_at: scheme?.start_at ?? null,
    codes,
    codeCodes,
  }
}

function nextValuesFromScheme(scheme: IdentifierScheme | null): NextValues {
  const map: NextValues = { prefix: {}, code: {} }
  for (const c of scheme?.codes ?? []) {
    map[c.slot][codeKey(c.source_kind, c.ref_key)] = c.next_value
  }
  return map
}

function mapsEqual(a: Record<string, string>, b: Record<string, string>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const k of keys) {
    if ((a[k] ?? '') !== (b[k] ?? '')) return false
  }
  return true
}

function draftsEqual(a: SchemeDraft, b: SchemeDraft): boolean {
  if (
    a.is_enabled !== b.is_enabled ||
    a.prefix_source !== b.prefix_source ||
    a.code_source !== b.code_source ||
    a.year_mode !== b.year_mode ||
    a.padding !== b.padding ||
    a.start_at !== b.start_at ||
    a.segment_order.join(',') !== b.segment_order.join(',')
  ) {
    return false
  }
  return mapsEqual(a.codes, b.codes) && mapsEqual(a.codeCodes, b.codeCodes)
}

function sourceOptionsFor(
  sources: IdentifierConfig['sources'],
  source: IdentifierPrefixSource | '',
): IdentifierSourceOption[] {
  if (source === 'category') return sources.categories
  if (source === 'product_template') return sources.product_templates
  return []
}

/** Codes a single slot contributes to the PUT payload. */
function slotCodes(
  slot: IdentifierSlot,
  source: IdentifierPrefixSource | '',
  map: Record<string, string>,
  sources: IdentifierConfig['sources'],
): IdentifierCodeInput[] {
  const out: IdentifierCodeInput[] = []
  if (!source) return out
  if (source === 'static') {
    const value = map[codeKey('static', '')]
    if (value) out.push({ slot, source_kind: 'static', ref_key: '', code: value })
    return out
  }
  for (const opt of sourceOptionsFor(sources, source)) {
    const value = map[codeKey(source, opt.ref_key)]
    if (value) out.push({ slot, source_kind: source, ref_key: opt.ref_key, code: value })
  }
  // Only the prefix slot carries a fallback.
  if (slot === 'prefix') {
    const fallback = map[codeKey('fallback', '')]
    if (fallback) out.push({ slot, source_kind: 'fallback', ref_key: '', code: fallback })
  }
  return out
}

function payloadFromDraft(
  draft: SchemeDraft,
  sources: IdentifierConfig['sources'],
): IdentifierSchemePayload {
  const hasCode = draft.segment_order.includes('code')
  const codes: IdentifierCodeInput[] = [
    ...slotCodes('prefix', draft.prefix_source, draft.codes, sources),
    ...(hasCode
      ? slotCodes('code', draft.code_source, draft.codeCodes, sources)
      : []),
  ]
  return {
    is_enabled: draft.is_enabled,
    segment_order: draft.segment_order,
    prefix_source: draft.prefix_source,
    code_source: hasCode ? draft.code_source : '',
    year_mode: draft.year_mode,
    padding: draft.padding,
    start_at: draft.start_at,
    codes,
  }
}

/** Every code a resource emits when enabled (feeds cross-resource checks). */
function emittedCodes(
  draft: SchemeDraft,
  sources: IdentifierConfig['sources'],
): string[] {
  if (!draft.is_enabled) return []
  const out = slotCodes('prefix', draft.prefix_source, draft.codes, sources).map(
    (c) => c.code,
  )
  if (draft.segment_order.includes('code')) {
    out.push(
      ...slotCodes('code', draft.code_source, draft.codeCodes, sources).map(
        (c) => c.code,
      ),
    )
  }
  return out
}

// ── Client-side validation (duplicates + cross-resource conflicts) ──

interface ResourceValidation {
  /** `${slot}:${kind}:${ref_key}` -> issue kind (conflict wins over dup). */
  flagged: Record<string, 'duplicate' | 'conflict'>
  dupCount: number
  conflictCount: number
}

const EMPTY_VALIDATION: ResourceValidation = {
  flagged: {},
  dupCount: 0,
  conflictCount: 0,
}

/** Flag key namespaced by slot, matching the server's `codes.<slot>.<kind>.<ref>`. */
const flagKey = (slot: IdentifierSlot, kind: IdentifierCodeSourceKind, refKey: string) =>
  `${slot}:${kind}:${refKey}`

function slotInputs(
  slot: IdentifierSlot,
  source: IdentifierPrefixSource | '',
  map: Record<string, string>,
  sources: IdentifierConfig['sources'],
): { key: string; value: string }[] {
  if (!source) return []
  if (source === 'static') {
    return [{ key: flagKey(slot, 'static', ''), value: map[codeKey('static', '')] ?? '' }]
  }
  const inputs = sourceOptionsFor(sources, source).map((opt) => ({
    key: flagKey(slot, source, opt.ref_key),
    value: map[codeKey(source, opt.ref_key)] ?? '',
  }))
  if (slot === 'prefix') {
    inputs.push({
      key: flagKey(slot, 'fallback', ''),
      value: map[codeKey('fallback', '')] ?? '',
    })
  }
  return inputs
}

function validateResource(
  draft: SchemeDraft,
  sources: IdentifierConfig['sources'],
  otherEmitted: string[],
): ResourceValidation {
  if (!draft.is_enabled) return EMPTY_VALIDATION
  const otherSet = new Set(otherEmitted)
  const flagged: Record<string, 'duplicate' | 'conflict'> = {}
  let dupCount = 0
  let conflictCount = 0

  const inputs = [
    ...slotInputs('prefix', draft.prefix_source, draft.codes, sources),
    ...(draft.segment_order.includes('code')
      ? slotInputs('code', draft.code_source, draft.codeCodes, sources)
      : []),
  ]
  // Duplicate check is global across both slots (the DB keeps every code
  // value distinct within a tenant).
  const counts: Record<string, number> = {}
  for (const input of inputs) {
    if (input.value) counts[input.value] = (counts[input.value] ?? 0) + 1
  }
  for (const input of inputs) {
    if (!input.value) continue
    const isDup = counts[input.value] > 1
    const isConflict = otherSet.has(input.value)
    if (isDup) dupCount++
    if (isConflict) conflictCount++
    if (isConflict) flagged[input.key] = 'conflict'
    else if (isDup) flagged[input.key] = 'duplicate'
  }
  return { flagged, dupCount, conflictCount }
}

// ── Formatting helpers ───────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear()

function yearPart(mode: IdentifierYearMode): string | null {
  if (mode === 'yyyy') return String(CURRENT_YEAR)
  if (mode === 'yy') return String(CURRENT_YEAR).slice(-2)
  return null
}

function padNum(n: number, width: number): string {
  return String(n).padStart(width, '0')
}

/** Render a full example ID by walking the segment order (empty parts drop out). */
function buildId(
  draft: Pick<SchemeDraft, 'segment_order' | 'year_mode' | 'padding'>,
  vals: { prefix?: string; code?: string },
  seq: number,
): string {
  const parts: string[] = []
  for (const seg of draft.segment_order) {
    if (seg === 'seq') parts.push(padNum(seq, draft.padding))
    else if (seg === 'year') {
      const y = yearPart(draft.year_mode)
      if (y) parts.push(y)
    } else if (seg === 'prefix') {
      if (vals.prefix) parts.push(vals.prefix)
    } else if (seg === 'code') {
      if (vals.code) parts.push(vals.code)
    }
  }
  return parts.join('-')
}

/** The first non-empty code in a slot - used to build representative previews. */
function firstSlotCode(
  source: IdentifierPrefixSource | '',
  map: Record<string, string>,
  sources: IdentifierConfig['sources'],
): string {
  if (!source) return ''
  if (source === 'static') return map[codeKey('static', '')] ?? ''
  for (const opt of sourceOptionsFor(sources, source)) {
    const v = map[codeKey(source, opt.ref_key)]
    if (v) return v
  }
  return map[codeKey('fallback', '')] ?? ''
}

/** Row sequence: `start_at` (offset by row index) wins over the live counter. */
function rowSeq(
  draft: SchemeDraft,
  liveNext: number | undefined,
  index: number,
): number {
  return draft.start_at != null ? draft.start_at + index : (liveNext ?? 1)
}

function cleanCode(value: string): string {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 8)
}

function extractFieldErrors(err: unknown): Record<string, string> | null {
  if (!axios.isAxiosError(err)) return null
  const data = err.response?.data as Record<string, unknown> | undefined
  const result = data?.result as Record<string, unknown> | null | undefined
  const raw = (data?.field_errors ?? result?.field_errors) as
    | Record<string, unknown>
    | undefined
  if (!raw || typeof raw !== 'object') return null
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(raw)) {
    out[k] = Array.isArray(v) ? String(v[0]) : String(v)
  }
  return Object.keys(out).length > 0 ? out : null
}

// ── Shared class fragments ───────────────────────────────

const SEGMENT_BUTTON_CLASS =
  'h-auto rounded-none border-0 border-b border-dashed border-b-transparent p-0 font-mono text-[22px] font-normal tracking-[0.01em] hover:bg-transparent hover:border-b-[var(--ink-3)] focus-visible:border-b-[var(--ink-3)] focus-visible:ring-0'

const CODE_INPUT_CLASS =
  'h-8 w-[92px] font-mono text-[13px] font-medium uppercase tracking-[0.05em] placeholder:normal-case placeholder:font-normal placeholder:tracking-normal'

const CODE_INPUT_ERROR_CLASS =
  'border-[var(--status-error)] bg-[var(--status-error-bg)]'

const POP_TITLE_CLASS =
  'px-2 pb-1.5 pt-1 text-[10.8px] uppercase tracking-[0.14em] text-[var(--ink-3)]'

// ── Page ─────────────────────────────────────────────────

export function IdentifiersSettingsTab() {
  const { t } = useTranslation()
  const { data, isPending, isError } = useIdentifierConfig()
  const updateScheme = useUpdateIdentifierScheme()

  const { serverDrafts, nextValues } = useMemo(
    () => ({
      serverDrafts: data
        ? ({
            holding: draftFromScheme('holding', data.schemes.holding),
            claim: draftFromScheme('claim', data.schemes.claim),
          } satisfies DraftMap)
        : null,
      nextValues: {
        holding: nextValuesFromScheme(data?.schemes.holding ?? null),
        claim: nextValuesFromScheme(data?.schemes.claim ?? null),
      },
    }),
    [data],
  )

  // `edits === null` means "no local changes"; drafts fall back to server state.
  const [edits, setEdits] = useState<DraftMap | null>(null)
  const [serverErrors, setServerErrors] = useState<
    Record<IdentifierResourceType, Record<string, string>>
  >({ holding: {}, claim: {} })
  const [saving, setSaving] = useState(false)

  const drafts = edits ?? serverDrafts

  const dirtyMap = useMemo(
    () => ({
      holding: Boolean(
        edits && serverDrafts && !draftsEqual(edits.holding, serverDrafts.holding),
      ),
      claim: Boolean(
        edits && serverDrafts && !draftsEqual(edits.claim, serverDrafts.claim),
      ),
    }),
    [edits, serverDrafts],
  )
  const isDirty = dirtyMap.holding || dirtyMap.claim

  const validation = useMemo(() => {
    if (!drafts || !data) {
      return { holding: EMPTY_VALIDATION, claim: EMPTY_VALIDATION }
    }
    const holdingEmitted = emittedCodes(drafts.holding, data.sources)
    const claimEmitted = emittedCodes(drafts.claim, data.sources)
    return {
      holding: validateResource(drafts.holding, data.sources, claimEmitted),
      claim: validateResource(drafts.claim, data.sources, holdingEmitted),
    }
  }, [drafts, data])

  const hasClientErrors =
    validation.holding.dupCount +
      validation.holding.conflictCount +
      validation.claim.dupCount +
      validation.claim.conflictCount >
    0

  const resourceName: Record<IdentifierResourceType, string> = {
    holding: t('identifiers.registrations.title', {
      defaultValue: 'Registrations',
    }),
    claim: t('identifiers.claims.title', { defaultValue: 'Claims' }),
  }

  function patchDraft(
    resource: IdentifierResourceType,
    patch: Partial<SchemeDraft>,
  ) {
    if (!drafts) return
    setEdits({ ...drafts, [resource]: { ...drafts[resource], ...patch } })
    setServerErrors((prev) => ({ ...prev, [resource]: {} }))
  }

  function patchCode(
    resource: IdentifierResourceType,
    slot: IdentifierSlot,
    kind: IdentifierCodeSourceKind,
    refKey: string,
    value: string,
  ) {
    if (!drafts) return
    const mapField = slot === 'prefix' ? 'codes' : 'codeCodes'
    setEdits({
      ...drafts,
      [resource]: {
        ...drafts[resource],
        [mapField]: {
          ...drafts[resource][mapField],
          [codeKey(kind, refKey)]: cleanCode(value),
        },
      },
    })
    setServerErrors((prev) => ({ ...prev, [resource]: {} }))
  }

  function handleDiscard() {
    setEdits(null)
    setServerErrors({ holding: {}, claim: {} })
  }

  async function handleSave() {
    if (!drafts || !data || saving) return
    const changed = RESOURCES.filter((r) => dirtyMap[r])
    if (changed.length === 0) return
    setSaving(true)
    const failed: Partial<
      Record<IdentifierResourceType, Record<string, string>>
    > = {}
    for (const resource of changed) {
      try {
        await updateScheme.mutateAsync({
          resourceType: resource,
          scheme: payloadFromDraft(drafts[resource], data.sources),
        })
      } catch (err) {
        const fieldErrors = extractFieldErrors(err)
        if (fieldErrors) {
          failed[resource] = fieldErrors
        } else {
          toastApiError(
            err,
            t('identifiers.savebar.error', {
              defaultValue: 'Failed to save identifier settings.',
            }),
          )
          failed[resource] = failed[resource] ?? {}
        }
      }
    }
    setSaving(false)
    if (Object.keys(failed).length > 0) {
      setServerErrors((prev) => ({ ...prev, ...failed }))
      toast.error(
        t('identifiers.savebar.reviewErrors', {
          defaultValue: 'Some codes need attention - review the flagged fields.',
        }),
      )
    } else {
      setEdits(null)
      setServerErrors({ holding: {}, claim: {} })
      toast.success(
        t('identifiers.savebar.saved', {
          defaultValue: 'Identifier settings saved.',
        }),
      )
    }
  }

  const saveSummary = hasClientErrors
    ? t('identifiers.savebar.fixErrors', {
        defaultValue: 'Fix the flagged codes before saving.',
      })
    : dirtyMap.holding && dirtyMap.claim
      ? t('identifiers.savebar.unsavedBoth', {
          defaultValue: 'Unsaved changes to Registrations and Claims',
        })
      : dirtyMap.holding
        ? t('identifiers.savebar.unsavedRegistrations', {
            defaultValue: 'Unsaved changes to Registrations',
          })
        : dirtyMap.claim
          ? t('identifiers.savebar.unsavedClaims', {
              defaultValue: 'Unsaved changes to Claims',
            })
          : t('identifiers.savebar.unsaved', { defaultValue: 'Unsaved changes' })

  return (
    <div className="max-w-[640px]">
      <h2 className="clara-heading">
        {t('identifiers.title', { defaultValue: 'Identifiers' })}
      </h2>
      <p className="clara-small mt-2 max-w-[60ch]">
        {t('identifiers.lede.before', {
          defaultValue:
            'Give registrations and claims numbers people can read over the phone, like',
        })}{' '}
        <span className="font-mono text-[11px] text-[var(--ink-2)]">
          {t('identifiers.lede.example', { defaultValue: 'RFG-2026-00012345' })}
        </span>
        {t('identifiers.lede.after', {
          defaultValue:
            '. Choose where the prefix comes from - the numbering takes care of itself.',
        })}
      </p>

      {/* ── How it works - scannable spec rows, not prose ── */}
      <div className="mb-8 mt-4 grid max-w-[62ch] grid-cols-[92px_1fr] gap-x-4 gap-y-1.5">
        <span className="pt-[3px] text-[10.8px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
          {t('identifiers.fine.permanentLabel', { defaultValue: 'Permanent' })}
        </span>
        <span className="clara-small">
          {t('identifiers.fine.permanent', {
            defaultValue:
              "Assigned at creation, never changes; Records created earlier aren't renumbered - ask us if you need that.",
          })}
        </span>
        <span className="pt-[3px] text-[10.8px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
          {t('identifiers.fine.formatLabel', { defaultValue: 'Format' })}
        </span>
        <span className="clara-small">
          {t('identifiers.fine.format', {
            defaultValue:
              'You arrange the parts, and choose the year style, digit count, and starting number.',
          })}
        </span>
        <span className="pt-[3px] text-[10.8px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
          {t('identifiers.fine.disjointLabel', { defaultValue: 'No overlap' })}
        </span>
        <span className="clara-small">
          {t('identifiers.fine.disjoint', {
            defaultValue:
              'Registration and claim prefixes must differ - overlaps are flagged before you can save.',
          })}
        </span>
      </div>

      {isPending ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
        </div>
      ) : isError || !drafts || !data ? (
        <p className="py-16 text-[13px] text-[var(--status-error)]">
          {t('identifiers.loadError', {
            defaultValue:
              'Could not load identifier settings. Try refreshing the page.',
          })}
        </p>
      ) : (
        <>
          {RESOURCES.map((resource, index) => (
            <ResourceSection
              key={resource}
              resource={resource}
              first={index === 0}
              draft={drafts[resource]}
              sources={data.sources}
              nextValues={nextValues[resource]}
              validation={validation[resource]}
              otherName={
                resourceName[resource === 'holding' ? 'claim' : 'holding']
              }
              fieldErrors={serverErrors[resource]}
              onPatch={(patch) => patchDraft(resource, patch)}
              onPatchCode={(slot, kind, refKey, value) =>
                patchCode(resource, slot, kind, refKey, value)
              }
            />
          ))}

          {/* ── Save ── */}
          <div className="mt-10 flex items-center gap-3 border-t-[0.5px] border-t-[var(--rule)] pt-5">
            <span
              className={cn(
                'flex-1 text-[12px]',
                hasClientErrors
                  ? 'text-[var(--status-error)]'
                  : 'text-[var(--ink-3)]',
              )}
            >
              {isDirty || hasClientErrors ? saveSummary : null}
            </span>
            {isDirty && (
              <Button
                variant="ghost"
                size="xs"
                onClick={handleDiscard}
                disabled={saving}
                className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
              >
                {t('identifiers.savebar.discard', { defaultValue: 'Discard' })}
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving || hasClientErrors || !isDirty}
              className="h-auto cursor-pointer rounded-[4px] bg-[var(--green)] px-5 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-40"
            >
              {saving && <Loader2 className="animate-spin" />}
              {t('identifiers.savebar.save', { defaultValue: 'Save changes' })}
            </Button>
          </div>

          {/* Voucher numbers save on their own: a separate scheme per type. */}
          {data.vouchers && (
            <VoucherIdentifiersSection vouchers={data.vouchers} />
          )}
        </>
      )}
    </div>
  )
}

// ── Per-resource section ─────────────────────────────────

interface ResourceSectionProps {
  resource: IdentifierResourceType
  first: boolean
  draft: SchemeDraft
  sources: IdentifierConfig['sources']
  nextValues: NextValues
  validation: ResourceValidation
  otherName: string
  fieldErrors: Record<string, string>
  onPatch: (patch: Partial<SchemeDraft>) => void
  onPatchCode: (
    slot: IdentifierSlot,
    kind: IdentifierCodeSourceKind,
    refKey: string,
    value: string,
  ) => void
}

function ResourceSection({
  resource,
  first,
  draft,
  sources,
  nextValues,
  validation,
  otherName,
  fieldErrors,
  onPatch,
  onPatchCode,
}: ResourceSectionProps) {
  const { t } = useTranslation()
  const prefixPickerRef = useRef<HTMLDivElement>(null)
  const codePickerRef = useRef<HTMLDivElement>(null)
  const flashTimer = useRef<number | null>(null)
  const [flashSlot, setFlashSlot] = useState<IdentifierSlot | null>(null)
  const [yearOpen, setYearOpen] = useState(false)
  const [numberOpen, setNumberOpen] = useState(false)

  const isHolding = resource === 'holding'
  const order = draft.segment_order
  const hasCode = order.includes('code')

  const title = isHolding
    ? t('identifiers.registrations.title', { defaultValue: 'Registrations' })
    : t('identifiers.claims.title', { defaultValue: 'Claims' })
  const subtitle = isHolding
    ? t('identifiers.registrations.subtitle', {
        defaultValue:
          "Every new registration gets a permanent ID the moment it's created.",
      })
    : t('identifiers.claims.subtitle', {
        defaultValue:
          'Claims get their own ID, separate from the registration they belong to.',
      })
  const offNote = isHolding
    ? t('identifiers.registrations.offNote', {
        defaultValue:
          "Off - new registrations won't get a readable number yet. Turn on anytime.",
      })
    : t('identifiers.claims.offNote', {
        defaultValue:
          "Off - claims won't get a readable number yet. Turn on anytime.",
      })

  const segmentLabel: Record<IdentifierSegment, string> = {
    prefix: t('identifiers.segment.prefix', { defaultValue: 'Prefix' }),
    year: t('identifiers.segment.year', { defaultValue: 'Year' }),
    code: t('identifiers.segment.code', { defaultValue: 'Code' }),
    seq: t('identifiers.segment.seq', { defaultValue: 'Number' }),
  }

  // Example values that make the specimen + cross-slot previews look complete.
  const prefixExample = firstSlotCode(draft.prefix_source, draft.codes, sources)
  const codeExample = hasCode
    ? firstSlotCode(draft.code_source, draft.codeCodes, sources)
    : ''
  const specimenSeq = rowSeq(draft, undefined, 0)

  function flashPicker(slot: IdentifierSlot) {
    setYearOpen(false)
    setNumberOpen(false)
    const ref = slot === 'prefix' ? prefixPickerRef : codePickerRef
    ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    if (flashTimer.current) window.clearTimeout(flashTimer.current)
    setFlashSlot(slot)
    flashTimer.current = window.setTimeout(() => setFlashSlot(null), 200)
  }

  // ── Reorderer handlers ──
  function moveSegment(seg: IdentifierSegment, dir: -1 | 1) {
    const i = order.indexOf(seg)
    const j = i + dir
    if (i < 0 || j < 0 || j >= order.length) return
    const next = [...order]
    ;[next[i], next[j]] = [next[j], next[i]]
    onPatch({ segment_order: next })
  }

  function addSegment(seg: IdentifierSegment) {
    if (order.includes(seg)) return
    const next = [...order]
    const seqIdx = next.indexOf('seq')
    next.splice(seqIdx >= 0 ? seqIdx : next.length, 0, seg)
    const patch: Partial<SchemeDraft> = { segment_order: next }
    if (seg === 'year' && draft.year_mode === 'none') patch.year_mode = 'yyyy'
    if (seg === 'code' && !draft.code_source) patch.code_source = 'category'
    onPatch(patch)
  }

  function removeSegment(seg: IdentifierSegment) {
    if (seg === 'seq') return
    const patch: Partial<SchemeDraft> = {
      segment_order: order.filter((s) => s !== seg),
    }
    if (seg === 'year') patch.year_mode = 'none'
    onPatch(patch)
  }

  const absentSegments = ALL_SEGMENTS.filter((s) => !order.includes(s))

  // ── Specimen segment nodes (rendered in the admin's order) ──
  function specimenNode(seg: IdentifierSegment) {
    if (seg === 'prefix') {
      return (
        <Button
          variant="ghost"
          onClick={() => flashPicker('prefix')}
          title={t('identifiers.specimen.prefixTitle', {
            defaultValue: 'Prefix source - set below',
          })}
          className={cn(
            SEGMENT_BUTTON_CLASS,
            'text-[var(--green)] hover:text-[var(--green)]',
          )}
        >
          {prefixExample || '···'}
        </Button>
      )
    }
    if (seg === 'code') {
      return (
        <Button
          variant="ghost"
          onClick={() => flashPicker('code')}
          title={t('identifiers.code.label', { defaultValue: 'Code comes from' })}
          className={cn(
            SEGMENT_BUTTON_CLASS,
            'text-[var(--green)] hover:text-[var(--green)]',
          )}
        >
          {codeExample || '···'}
        </Button>
      )
    }
    if (seg === 'year') {
      return (
        <Popover open={yearOpen} onOpenChange={setYearOpen}>
          <PopoverTrigger
            render={
              <Button
                variant="ghost"
                title={t('identifiers.specimen.yearTitle', {
                  defaultValue: 'Year format',
                })}
                className={cn(
                  SEGMENT_BUTTON_CLASS,
                  'text-[var(--ink)] hover:text-[var(--ink)]',
                )}
              >
                {yearPart(draft.year_mode) ?? String(CURRENT_YEAR)}
              </Button>
            }
          />
          <YearPopoverContent
            yearMode={draft.year_mode}
            onSelect={(year_mode) => {
              setYearOpen(false)
              if (year_mode === 'none') removeSegment('year')
              else onPatch({ year_mode })
            }}
          />
        </Popover>
      )
    }
    // seq
    return (
      <Popover open={numberOpen} onOpenChange={setNumberOpen}>
        <PopoverTrigger
          render={
            <Button
              variant="ghost"
              title={t('identifiers.specimen.numberTitle', {
                defaultValue: 'Number format',
              })}
              className={cn(
                SEGMENT_BUTTON_CLASS,
                'text-[var(--ink)] hover:text-[var(--ink)]',
              )}
            >
              {padNum(specimenSeq, draft.padding)}
            </Button>
          }
        />
        <NumberPopoverContent
          resource={resource}
          padding={draft.padding}
          startAt={draft.start_at}
          onPaddingChange={(padding) => onPatch({ padding })}
          onStartAtChange={(start_at) => onPatch({ start_at })}
        />
      </Popover>
    )
  }

  // Server errors that no rendered input claims (segment_order, padding, ...).
  const codeKeyPrefixes = new Set(['codes.'])
  const sectionErrors = Object.entries(fieldErrors).filter(
    ([key]) => !Array.from(codeKeyPrefixes).some((p) => key.startsWith(p)),
  )

  return (
    <section className={cn(!first && 'mt-10')}>
      {/* ── Section label + enable toggle row ── */}
      <p className="clara-section-label mb-4">{title}</p>
      <Button
        variant="ghost"
        type="button"
        onClick={() => onPatch({ is_enabled: !draft.is_enabled })}
        className="h-auto w-full justify-start gap-3 whitespace-normal rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-4 py-3 text-left transition-[border-color] duration-150 hover:border-[var(--green-border)]"
      >
        <Switch checked={draft.is_enabled} decorative aria-label={title} />
        <div>
          <span className="block text-[13px] text-[var(--ink)]">{subtitle}</span>
          {!draft.is_enabled && (
            <span className="mt-0.5 block text-[11px] text-[var(--ink-3)]">
              {offNote}
            </span>
          )}
        </div>
      </Button>

      {sectionErrors.length > 0 && (
        <div className="mt-3">
          {sectionErrors.map(([key, message]) => (
            <p key={key} className="text-[12px] text-[var(--status-error)]">
              {message}
            </p>
          ))}
        </div>
      )}

      {draft.is_enabled && (
        <div>
          {/* ── Specimen ── */}
          <div className="mt-3 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-4 py-3.5">
            <div className="flex items-baseline">
              {order.map((seg, i) => (
                <Fragment key={seg}>
                  {i > 0 && (
                    <span className="px-[0.12em] font-mono text-[22px] text-[var(--ink-3)]">
                      -
                    </span>
                  )}
                  {specimenNode(seg)}
                </Fragment>
              ))}
            </div>
            <div className="mt-2.5 text-[10.8px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
              <span className="font-display text-[13px] italic normal-case tracking-[0.02em]">
                {t('identifiers.specimen.editHint', {
                  defaultValue: 'click a part to change it',
                })}
              </span>
            </div>
            <p className="mt-3 max-w-[52ch] text-[12px] font-light text-[var(--ink-3)]">
              {t('identifiers.specimen.note', {
                defaultValue:
                  'The counter resets whenever the parts before the number change (per year, per code). Once assigned, an ID never changes.',
              })}
            </p>
          </div>

          {/* ── Pattern order (reorderer) ── */}
          <div className="mt-5">
            <div className="mb-2.5 text-[10.8px] uppercase tracking-[0.16em] text-[var(--ink-3)]">
              {t('identifiers.order.label', { defaultValue: 'Pattern' })}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {order.map((seg, i) => (
                <div
                  key={seg}
                  className="flex items-center gap-1 rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] py-1 pl-2.5 pr-1"
                >
                  <span className="text-[12px] text-[var(--ink)]">
                    {segmentLabel[seg]}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    type="button"
                    disabled={i === 0}
                    aria-label={t('identifiers.order.moveLeft', {
                      defaultValue: 'Move left',
                    })}
                    onClick={() => moveSegment(seg, -1)}
                    className="text-[var(--ink-3)] hover:text-[var(--ink)] disabled:opacity-30"
                  >
                    <ChevronLeft className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    type="button"
                    disabled={i === order.length - 1}
                    aria-label={t('identifiers.order.moveRight', {
                      defaultValue: 'Move right',
                    })}
                    onClick={() => moveSegment(seg, 1)}
                    className="text-[var(--ink-3)] hover:text-[var(--ink)] disabled:opacity-30"
                  >
                    <ChevronRight className="size-3.5" />
                  </Button>
                  {seg !== 'seq' && (
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      type="button"
                      aria-label={t('identifiers.order.remove', {
                        defaultValue: 'Remove',
                      })}
                      onClick={() => removeSegment(seg)}
                      className="text-[var(--ink-3)] hover:text-[var(--status-error)]"
                    >
                      <X className="size-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {absentSegments.length > 0 && (
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  {t('identifiers.order.add', { defaultValue: 'Add' })}
                </span>
                {absentSegments.map((seg) => (
                  <Button
                    key={seg}
                    variant="outline"
                    size="xs"
                    type="button"
                    onClick={() => addSegment(seg)}
                    className="gap-1 rounded-[6px] border-[0.5px] border-[var(--rule)] text-[12px] text-[var(--ink-2)] hover:border-[var(--green-border)] hover:text-[var(--green)]"
                  >
                    <Plus className="size-3" />
                    {segmentLabel[seg]}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* ── Codes (prefix + code in one place) ── */}
          <CodeEntry
            isHolding={isHolding}
            hasCode={hasCode}
            draft={draft}
            sources={sources}
            nextValues={nextValues}
            validation={validation}
            fieldErrors={fieldErrors}
            otherName={otherName}
            prefixPickerRef={prefixPickerRef}
            codePickerRef={codePickerRef}
            flashSlot={flashSlot}
            onSetPrefixSource={(prefix_source) => onPatch({ prefix_source })}
            onSetCodeSource={(code_source) => onPatch({ code_source })}
            onPatchCode={onPatchCode}
          />

          {/* ── Disjointness note (claims only) ── */}
          {!isHolding && (
            <div className="mt-3.5 max-w-[56ch] rounded-md border-[0.5px] border-[var(--gold-border)] bg-[var(--gold-bg)] px-3.5 py-2 text-[12.2px] font-light text-[var(--ink-2)]">
              {t('identifiers.claims.sharedNote', {
                defaultValue:
                  'Same parts as registrations, separate codes. A claim code that matches a registration code is flagged as an error.',
              })}
            </div>
          )}
        </div>
      )}
    </section>
  )
}

// ── Codes: prefix + code slots in one place ──────────────

function SlotSourcePicker({
  label,
  desc,
  source,
  onSetSource,
  pickerRef,
  flash,
}: {
  label: string
  desc: string
  source: IdentifierPrefixSource
  onSetSource: (source: IdentifierPrefixSource) => void
  pickerRef: React.RefObject<HTMLDivElement | null>
  flash: boolean
}) {
  const { t } = useTranslation()
  return (
    <div>
      <div className="mb-2.5 text-[10.8px] uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {label}
      </div>
      <div
        ref={pickerRef}
        className={cn(
          'inline-block rounded-lg',
          flash
            ? 'shadow-[0_0_0_4px_var(--green-bg),0_0_0_5px_var(--green-border)] transition-none'
            : 'shadow-none transition-shadow duration-1000',
        )}
      >
        <Tabs
          value={source}
          onValueChange={(value) => onSetSource(value as IdentifierPrefixSource)}
        >
          <TabsList variant="pill">
            <TabsTrigger value="category" className="px-4">
              {t('identifiers.prefixSource.category', {
                defaultValue: 'Product category',
              })}
            </TabsTrigger>
            <TabsTrigger value="product_template" className="px-4">
              {t('identifiers.prefixSource.template', {
                defaultValue: 'Product type',
              })}
            </TabsTrigger>
            <TabsTrigger value="static" className="px-4">
              {t('identifiers.prefixSource.fixed', { defaultValue: 'Fixed' })}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <p className="mt-2.5 min-h-[20px] text-[12.5px] font-light text-[var(--ink-3)]">
        {desc}
      </p>
    </div>
  )
}

interface CodeEntryProps {
  isHolding: boolean
  hasCode: boolean
  draft: SchemeDraft
  sources: IdentifierConfig['sources']
  nextValues: NextValues
  validation: ResourceValidation
  fieldErrors: Record<string, string>
  otherName: string
  prefixPickerRef: React.RefObject<HTMLDivElement | null>
  codePickerRef: React.RefObject<HTMLDivElement | null>
  flashSlot: IdentifierSlot | null
  onSetPrefixSource: (source: IdentifierPrefixSource) => void
  onSetCodeSource: (source: IdentifierPrefixSource) => void
  onPatchCode: (
    slot: IdentifierSlot,
    kind: IdentifierCodeSourceKind,
    refKey: string,
    value: string,
  ) => void
}

interface ActiveSlot {
  slot: IdentifierSlot
  source: IdentifierPrefixSource
  map: Record<string, string>
  showFallback: boolean
  label: string
}

const FALLBACK_REF = ' fallback'

function CodeEntry({
  isHolding,
  hasCode,
  draft,
  sources,
  validation,
  fieldErrors,
  otherName,
  prefixPickerRef,
  codePickerRef,
  flashSlot,
  onSetPrefixSource,
  onSetCodeSource,
  onPatchCode,
}: CodeEntryProps) {
  const { t } = useTranslation()
  const prefixSource = draft.prefix_source
  const codeSource = (draft.code_source || 'category') as IdentifierPrefixSource

  const active: ActiveSlot[] = [
    {
      slot: 'prefix',
      source: prefixSource,
      map: draft.codes,
      showFallback: true,
      label: t('identifiers.segment.prefix', { defaultValue: 'Prefix' }),
    },
    ...(hasCode
      ? [
          {
            slot: 'code' as IdentifierSlot,
            source: codeSource,
            map: draft.codeCodes,
            showFallback: false,
            label: t('identifiers.segment.code', { defaultValue: 'Code' }),
          },
        ]
      : []),
  ]
  const prefixA = active.find((a) => a.slot === 'prefix')
  const codeA = active.find((a) => a.slot === 'code')

  const exampleOf = (a: ActiveSlot) => firstSlotCode(a.source, a.map, sources)

  function ctxValue(
    a: ActiveSlot | undefined,
    groupSource: IdentifierPrefixSource | 'static',
    refKey: string,
  ): string {
    if (!a) return ''
    if (a.source === 'static') return a.map[codeKey('static', '')] ?? ''
    if (a.source === groupSource) {
      const v = a.map[codeKey(groupSource, refKey)] ?? ''
      if (v) return v
      return a.showFallback ? (a.map[codeKey('fallback', '')] ?? '') : ''
    }
    return exampleOf(a)
  }

  function fullPreview(
    groupSource: IdentifierPrefixSource | 'static',
    refKey: string,
    seq: number,
  ): string {
    return buildId(
      draft,
      {
        prefix: ctxValue(prefixA, groupSource, refKey),
        code: ctxValue(codeA, groupSource, refKey),
      },
      seq,
    )
  }

  function inlineError(
    slot: IdentifierSlot,
    kind: IdentifierCodeSourceKind,
    refKey: string,
    value: string,
  ): string | null {
    const serverKey = `codes.${slot}.${kind}.${refKey}`
    if (fieldErrors[serverKey]) return fieldErrors[serverKey]
    const issue = validation.flagged[flagKey(slot, kind, refKey)]
    if (!issue || !value) return null
    if (issue === 'conflict') {
      return t('identifiers.grid.conflictInline', {
        defaultValue: '"{{code}}" is already used by {{other}}',
        code: value,
        other: otherName,
      })
    }
    return t('identifiers.grid.duplicateInline', {
      defaultValue: '"{{code}}" is used more than once in this list',
      code: value,
    })
  }

  const staticSlots = active.filter((a) => a.source === 'static')
  const rowSlots = active.filter((a) => a.source !== 'static')
  const groupSources = Array.from(new Set(rowSlots.map((a) => a.source)))
  const staticSeq = rowSeq(draft, undefined, 0)

  const coverageProblem = (() => {
    const parts: string[] = []
    if (validation.dupCount > 0)
      parts.push(
        validation.dupCount === 1
          ? t('identifiers.grid.duplicateOne', { defaultValue: '1 duplicate code' })
          : t('identifiers.grid.duplicateMany', {
              defaultValue: '{{n}} duplicate codes',
              n: validation.dupCount,
            }),
      )
    if (validation.conflictCount > 0)
      parts.push(
        t('identifiers.grid.conflictCount', {
          defaultValue: '{{n}} already used by {{other}}',
          n: validation.conflictCount,
          other: otherName,
        }),
      )
    return parts.join(' · ')
  })()

  const codeInput = (
    a: ActiveSlot,
    kind: IdentifierCodeSourceKind,
    refKey: string,
  ) => {
    const value = a.map[codeKey(kind, refKey)] ?? ''
    const err = inlineError(a.slot, kind, refKey, value)
    const placeholder =
      a.slot === 'prefix'
        ? t('identifiers.grid.prefixPlaceholder', { defaultValue: 'prefix' })
        : t('identifiers.grid.codePlaceholder', { defaultValue: 'code' })
    return (
      <Input
        value={value}
        maxLength={8}
        placeholder={placeholder}
        aria-invalid={Boolean(err) || undefined}
        onChange={(e) => onPatchCode(a.slot, kind, refKey, e.target.value)}
        className={cn(CODE_INPUT_CLASS, err && CODE_INPUT_ERROR_CLASS)}
      />
    )
  }

  return (
    <div className="mt-5 flex flex-col gap-4">
      <SlotSourcePicker
        label={t('identifiers.prefixSource.label', {
          defaultValue: 'Prefix comes from',
        })}
        desc={prefixSourceDesc(t, isHolding, prefixSource)}
        source={prefixSource}
        onSetSource={onSetPrefixSource}
        pickerRef={prefixPickerRef}
        flash={flashSlot === 'prefix'}
      />
      {hasCode && (
        <SlotSourcePicker
          label={t('identifiers.code.label', { defaultValue: 'Code comes from' })}
          desc={codeSourceDesc(t, codeSource)}
          source={codeSource}
          onSetSource={onSetCodeSource}
          pickerRef={codePickerRef}
          flash={flashSlot === 'code'}
        />
      )}

      {/* Fixed (static) slots: one input each + a combined preview. */}
      {staticSlots.length > 0 && (
        <div className="flex flex-col gap-2.5">
          {staticSlots.map((a) => {
            const err = inlineError(
              a.slot,
              'static',
              '',
              a.map[codeKey('static', '')] ?? '',
            )
            return (
              <div key={a.slot}>
                <div className="flex items-center gap-3">
                  <span className="w-[54px] text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
                    {a.label}
                  </span>
                  {codeInput(a, 'static', '')}
                </div>
                {err && (
                  <p className="mt-1 pl-[66px] text-[12px] text-[var(--status-error)]">
                    {err}
                  </p>
                )}
              </div>
            )
          })}
          <div className="flex items-center gap-3 pl-[66px]">
            <span className="text-[14px] text-[var(--ink-3)]">{'→'}</span>
            <span className="font-mono text-[15px] text-[var(--ink)]">
              {fullPreview('static', '', staticSeq)}
            </span>
          </div>
        </div>
      )}

      {/* Per-row grids: slots that share a source merge into one table. */}
      {groupSources.map((groupSource) => {
        const group = rowSlots.filter((a) => a.source === groupSource)
        const options = sourceOptionsFor(sources, groupSource)
        const hasPrefix = group.some((a) => a.slot === 'prefix')
        const twoCol = group.length > 1
        const cols = twoCol
          ? 'grid-cols-[minmax(130px,1fr)_92px_92px_minmax(140px,1fr)]'
          : 'grid-cols-[minmax(160px,1fr)_110px_minmax(190px,1fr)]'
        const filled = options.filter((opt) =>
          group.every((a) => a.map[codeKey(groupSource, opt.ref_key)]),
        ).length
        const missing = options.length - filled
        return (
          <div key={groupSource}>
            {twoCol && (
              <div className={cn('grid items-end gap-5 pb-1', cols)}>
                <span />
                {group.map((a) => (
                  <span
                    key={a.slot}
                    className="text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)]"
                  >
                    {a.label}
                  </span>
                ))}
                <span />
              </div>
            )}

            {hasPrefix && (
              <div className="border-b-[0.5px] border-t-[0.5px] border-[var(--rule)]">
                <div className={cn('grid items-center gap-5 py-[11px]', cols)}>
                  <div className="font-display text-[15px] italic text-[var(--ink-3)]">
                    {t('identifiers.grid.fallbackName', {
                      defaultValue: 'anything without a code',
                    })}
                  </div>
                  {group.map((a) =>
                    a.slot === 'prefix' ? (
                      <Fragment key={a.slot}>{codeInput(a, 'fallback', '')}</Fragment>
                    ) : (
                      <span
                        key={a.slot}
                        className="text-center text-[12px] text-[var(--ink-3)]"
                      >
                        {'-'}
                      </span>
                    ),
                  )}
                  <div className="text-right font-mono text-[12.5px] text-[var(--ink-2)]">
                    {fullPreview(groupSource, FALLBACK_REF, staticSeq)}
                  </div>
                </div>
                {inlineError(
                  'prefix',
                  'fallback',
                  '',
                  draft.codes[codeKey('fallback', '')] ?? '',
                ) && (
                  <p className="pb-2 text-[11.5px] text-[var(--status-error)]">
                    {inlineError(
                      'prefix',
                      'fallback',
                      '',
                      draft.codes[codeKey('fallback', '')] ?? '',
                    )}
                  </p>
                )}
              </div>
            )}

            {options.map((opt, index) => {
              const seq = rowSeq(draft, undefined, index)
              const rowErr = group
                .map((a) =>
                  inlineError(
                    a.slot,
                    groupSource,
                    opt.ref_key,
                    a.map[codeKey(groupSource, opt.ref_key)] ?? '',
                  ),
                )
                .find(Boolean)
              return (
                <div
                  key={opt.ref_key}
                  className={cn(
                    'border-b-[0.5px] border-[var(--rule)]',
                    !hasPrefix && index === 0 && 'border-t-[0.5px]',
                  )}
                >
                  <div className={cn('grid items-center gap-5 py-[11px]', cols)}>
                    <div className="text-[13.5px] text-[var(--ink)]">
                      {opt.label}
                      <span className="block text-[11px] tracking-[0.02em] text-[var(--ink-3)]">
                        {groupSource === 'category'
                          ? t('identifiers.grid.productCount', {
                              defaultValue: '{{n}} products',
                              n: opt.product_count.toLocaleString(),
                            })
                          : t('identifiers.grid.templateMeta', {
                              defaultValue: 'Product type · {{n}} SKUs',
                              n: opt.product_count.toLocaleString(),
                            })}
                      </span>
                    </div>
                    {group.map((a) => (
                      <Fragment key={a.slot}>
                        {codeInput(a, groupSource, opt.ref_key)}
                      </Fragment>
                    ))}
                    <div className="text-right font-mono text-[12.5px] text-[var(--ink-2)]">
                      {fullPreview(groupSource, opt.ref_key, seq)}
                    </div>
                  </div>
                  {rowErr && (
                    <p className="pb-2 text-[11.5px] text-[var(--status-error)]">
                      {rowErr}
                    </p>
                  )}
                </div>
              )
            })}

            <div className="mt-3 flex items-baseline gap-3.5 text-[12px] text-[var(--ink-3)]">
              <span>
                {t('identifiers.grid.coverage', {
                  defaultValue: '{{set}} of {{total}} have a code',
                  set: filled,
                  total: options.length,
                })}
                {hasPrefix && missing > 0 && !twoCol && (
                  <>
                    {' · '}
                    {t('identifiers.grid.coverageFallback', {
                      defaultValue: '{{n}} will use the fallback',
                      n: missing,
                    })}
                  </>
                )}
              </span>
              {coverageProblem && (
                <span className="text-[var(--status-error)]">{coverageProblem}</span>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Source descriptions ──────────────────────────────────

function prefixSourceDesc(
  t: ReturnType<typeof useTranslation>['t'],
  isHolding: boolean,
  source: IdentifierPrefixSource,
): string {
  if (isHolding) {
    if (source === 'category')
      return t('identifiers.registrations.sourceDesc.category', {
        defaultValue:
          'Your categories, your codes. New categories show up here automatically.',
      })
    if (source === 'product_template')
      return t('identifiers.registrations.sourceDesc.template', {
        defaultValue:
          'Codes follow the product type - useful when types cut across categories.',
      })
    return t('identifiers.registrations.sourceDesc.fixed', {
      defaultValue: 'One running number across all registrations.',
    })
  }
  if (source === 'category')
    return t('identifiers.claims.sourceDesc.category', {
      defaultValue:
        'Your categories, their claim codes - different from the registration codes.',
    })
  if (source === 'product_template')
    return t('identifiers.claims.sourceDesc.template', {
      defaultValue:
        'Your product types, their claim codes - different from the registration codes.',
    })
  return t('identifiers.claims.sourceDesc.fixed', {
    defaultValue: 'One running number across all claims.',
  })
}

function codeSourceDesc(
  t: ReturnType<typeof useTranslation>['t'],
  source: IdentifierPrefixSource,
): string {
  if (source === 'category')
    return t('identifiers.code.sourceDesc.category', {
      defaultValue: "The code follows the product's category.",
    })
  if (source === 'product_template')
    return t('identifiers.code.sourceDesc.template', {
      defaultValue: 'The code follows the product type.',
    })
  return t('identifiers.code.sourceDesc.fixed', {
    defaultValue: 'One fixed code on every record.',
  })
}

// ── Specimen popovers ────────────────────────────────────

function YearPopoverContent({
  yearMode,
  onSelect,
}: {
  yearMode: IdentifierYearMode
  onSelect: (mode: IdentifierYearMode) => void
}) {
  const { t } = useTranslation()
  const options: {
    value: IdentifierYearMode
    sample: string
    mono: boolean
    hint: string
  }[] = [
    {
      value: 'yyyy',
      sample: String(CURRENT_YEAR),
      mono: true,
      hint: t('identifiers.year.full', { defaultValue: 'full year' }),
    },
    {
      value: 'yy',
      sample: String(CURRENT_YEAR).slice(-2),
      mono: true,
      hint: t('identifiers.year.short', { defaultValue: 'short year' }),
    },
    {
      value: 'none',
      sample: t('identifiers.year.none', { defaultValue: 'No year' }),
      mono: false,
      hint: t('identifiers.year.noneHint', {
        defaultValue: 'numbers never reset',
      }),
    },
  ]
  return (
    <PopoverContent align="start" className="min-w-[210px] p-1.5">
      <div className={POP_TITLE_CLASS}>
        {t('identifiers.year.title', { defaultValue: 'Year' })}
      </div>
      {options.map((opt) => {
        const active = opt.value === yearMode
        return (
          <Button
            key={opt.value}
            variant="ghost"
            onClick={() => onSelect(opt.value)}
            className={cn(
              'h-auto w-full justify-between gap-4 rounded-[5px] border-[0.5px] border-solid border-transparent px-2 py-1.5 text-[13px] font-light',
              active
                ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)] hover:bg-[var(--green-bg)] hover:text-[var(--green)]'
                : 'text-[var(--ink)]',
            )}
          >
            <span className={cn(opt.mono && 'font-mono', 'text-[13px]')}>
              {opt.sample}
            </span>
            <span
              className={cn(
                'text-[11px]',
                active ? 'text-[var(--green)] opacity-75' : 'text-[var(--ink-3)]',
              )}
            >
              {opt.hint}
            </span>
          </Button>
        )
      })}
    </PopoverContent>
  )
}

function NumberPopoverContent({
  resource,
  padding,
  startAt,
  onPaddingChange,
  onStartAtChange,
}: {
  resource: IdentifierResourceType
  padding: number
  startAt: number | null
  onPaddingChange: (padding: number) => void
  onStartAtChange: (startAt: number | null) => void
}) {
  const { t } = useTranslation()
  const startInputId = `identifiers-start-${resource}`
  return (
    <PopoverContent align="start" className="min-w-[210px] p-1.5">
      <div className={POP_TITLE_CLASS}>
        {t('identifiers.number.title', { defaultValue: 'Number' })}
      </div>
      <div className="flex items-center justify-center gap-3 px-2 pb-2.5 pt-1">
        <Button
          variant="outline"
          size="icon-xs"
          aria-label="-"
          onClick={() => onPaddingChange(Math.max(4, padding - 1))}
        >
          -
        </Button>
        <span className="min-w-[64px] text-center text-[12.5px] text-[var(--ink)]">
          {t('identifiers.number.digits', {
            defaultValue: '{{n}} digits',
            n: padding,
          })}
        </span>
        <Button
          variant="outline"
          size="icon-xs"
          aria-label="+"
          onClick={() => onPaddingChange(Math.min(10, padding + 1))}
        >
          +
        </Button>
      </div>
      <div className="border-t-[0.5px] border-[var(--rule)] px-2 pb-1.5 pt-2">
        <Label
          htmlFor={startInputId}
          className="text-[11px] font-light uppercase tracking-[0.08em] text-[var(--ink-3)]"
        >
          {t('identifiers.number.startFrom', {
            defaultValue: 'Start counting from',
          })}
        </Label>
        <Input
          id={startInputId}
          inputMode="numeric"
          placeholder="1"
          value={startAt != null ? String(startAt) : ''}
          onChange={(e) => {
            const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 12)
            onStartAtChange(digits ? parseInt(digits, 10) : null)
          }}
          className="mt-1.5 h-7 w-[120px] font-mono text-[12.5px]"
        />
      </div>
    </PopoverContent>
  )
}
